import { v4 as uuid } from 'uuid';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import { inject, Injectable } from '@angular/core';
import { exhaustMap, map, tap } from 'rxjs/operators';
import { BusinessUnitService } from './business-unit.service';
import { Dtos } from '#models';
import { Server } from '#enums';
import { StringService } from '../string.service';
import { ResponseValidator } from '../response-validator.service';

@Injectable({
  providedIn: 'root',
})
export class PSSRResponseService {
  private http = inject(HttpClient);
  private businessUnitService = inject(BusinessUnitService);
  private responseValidator = inject(ResponseValidator);
  private _stringService = inject(StringService);

  public create(pssrResponse: Dtos.PSSRResponse): Observable<Dtos.PSSRResponse> {
    return this.http.post<Dtos.PSSRResponse>(`${environment.apiUrl}/response`, pssrResponse);
  }

  public update(pssrResponse: Dtos.PSSRResponse): Observable<Dtos.PSSRResponse> {
    return this.http.put<Dtos.PSSRResponse>(`${environment.apiUrl}/response`, { ...pssrResponse, ActionItems: [] });
  }

  public clearResponse(pssrResponse: Dtos.PSSRResponse): Observable<Dtos.PSSRResponse> {
    return this.http.put<Dtos.PSSRResponse>(`${environment.apiUrl}/response/clearResponse`, pssrResponse);
  }

  public delete(responseId: number) {
    return this.http.delete<Dtos.PSSRResponse[]>(`${environment.apiUrl}/response/${responseId}`);
  }

  public getAllByPSSRId(pssrId: number): Observable<Dtos.PSSRResponse[]> {
    return this.http.get<Dtos.PSSRResponse[]>(`${environment.apiUrl}/response/pssrResponses/${pssrId}`);
  }

  public createByPSSRSection(pssrId: number, section: Dtos.TaskPlanSection): Observable<Dtos.PSSRResponse[]> {
    return this.http.post<Dtos.PSSRResponse[]>(`${environment.apiUrl}/response/createByPSSRSection/${pssrId}`, section);
  }

  public deleteByPSSRSection(pssrId: number, taskPlanSectionId: number, taskPlanSectionDescription?: string) {
    let params = {};
    if (taskPlanSectionDescription !== undefined) {
      params = {
        params: {
          taskPlanSectionDescription,
        },
      };
    }
    return this.http.delete<Dtos.PSSRResponse[]>(
      `${environment.apiUrl}/response/deleteByPSSRSection/${pssrId}/${taskPlanSectionId}`,
      params
    );
  }

  public getStandAloneItems(pssrId: number): Observable<Dtos.ResponseItem[]> {
    return this.http.get<Dtos.ResponseItem[]>(`${environment.apiUrl}/response/standAloneItems/${pssrId}`);
  }

  public updateActionItem(responseItem: Partial<Dtos.ResponseItem>): Observable<Dtos.ResponseItem> {
    return this.http
      .post<Dtos.ResponseItem>(`${environment.apiUrl}/response/updateActionItem`, {
        ...responseItem,
        Comment: this._stringService.toHtmlEntities(responseItem.Comment),
        DueDate: responseItem.DueDate ? new Date(responseItem.DueDate).toISOString() : null,
      })
      .pipe(
        map(res => {
          return { ...res, DueDate: res.DueDate && new Date(res.DueDate) };
        })
      );
  }

  public deleteResponseItem(itemId: number): Observable<Dtos.ResponseItem> {
    return this.http.delete<Dtos.ResponseItem>(`${environment.apiUrl}/response/deleteResponseItem/${itemId}`);
  }

  public getPagedActionItems(
    filter: Dtos.ActionItemFilter,
    pagination: Dtos.PaginationRequest
  ): Observable<Dtos.PagedList<Dtos.PSSR>> {
    const requestParams = {
      Filter: filter,
      Pagination: pagination,
    };

    return this.http.post<Dtos.PagedList<Dtos.PSSR>>(`${environment.apiUrl}/response/paged-list`, requestParams);
  }

  public getImagesByActionItem(actionItemId: number): Observable<Dtos.ResponseItemImage[]> {
    return this.http.get<Dtos.ResponseItemImage[]>(`${environment.apiUrl}/response/getImageList/${actionItemId}`);
  }

  public getThumbnailList(actionItemId: number): Observable<Dtos.ResponseItemImage[]> {
    return this.http.get<Dtos.ResponseItemImage[]>(`${environment.apiUrl}/response/getThumbnailList/${actionItemId}`);
  }

  public getImageByGUID(responseItemId: number, imageGUID: string): Observable<Dtos.ResponseItemImage> {
    return this.http.get<Dtos.ResponseItemImage>(
      `${environment.apiUrl}/response/${responseItemId}/getImage2/${imageGUID}`
    );
  }

  public export(filter: Dtos.ActionItemFilter): Observable<object> {
    const result = this.http.post<object>(`${environment.apiUrl}/response/downloadExcel`, filter, {
      responseType: 'blob' as 'json',
    });
    return result;
  }

  isBlankSection(section: Dtos.TaskPlanSection) {
    return section.Type === Server.SectionType.StandAlone
      ? section.StandAloneItems.length === 0
      : section.Responses.length === 0;
  }
  isVisibleSection(section: Dtos.TaskPlanSection, pssr: Dtos.PSSR) {
    switch (section.Type) {
    case Server.SectionType.Body: {
      return !this.isBlankSection(section);
    }
    case Server.SectionType.ProjectSpecific: {
      return (
        !this.isBlankSection(section) ||
          ((pssr.Status === Server.PSSRStatus.Draft || pssr.Status === Server.PSSRStatus.Initiated) &&
            pssr.CurrentUser.CanEdit)
      );
    }
    case Server.SectionType.StandAlone: {
      if (pssr.Status >= Server.PSSRStatus.Approved && this.isBlankSection(section)) {
        return false;
      }

      if (pssr.Status < Server.PSSRStatus.Completed) {
        return false;
      }

      return true;
    }
    case Server.SectionType.Signature: {
      return pssr.Status !== Server.PSSRStatus.Draft && !this.isBlankSection(section);
    }
    }
  }
  uploadImage(image: Dtos.ResponseItemImage) {
    return this.http.post(`${environment.apiUrl}/response/upload-image`, image);
  }

  deleteImage(image: Dtos.ResponseItemImage): Observable<void> {
    return this.uploadImage({ ...image, IsDeleted: true }).pipe(map(() => void 0));
  }

  signResponse({
    response,
    signature,
    signatureDate,
    signedBy,
  }: {
    response: Dtos.PSSRResponse;
    signature: string;
    signatureDate: Date;
    signedBy: string;
  }): Observable<Dtos.PSSRResponse> {
    return this.updateActionItem({
      Id: 0,
      Priority: 0,
      Status: 0,
      Comment: `Completed on Web by: ${signedBy}, at: ${signatureDate.toISOString()}`,
      IsActionItem: false,
      ResponseID: response.Id!,
      GUID: uuid(),
      PSSRID: response.PSSRID,
      CreateDate: signatureDate,
      Links: [],
    }).pipe(
      tap(responseItem => (response.ActionItems = [responseItem])),
      exhaustMap(responseItem => {
        return this.uploadImage({
          GUID: uuid(),
          Id: 0,
          Photo: signature,
          ResponseItemGUID: null,
          ImageType: 1,
          ResponseItemID: responseItem.Id,
          PSSRID: response.PSSRID,
          Caption: signedBy,
          CreateDate: signatureDate,
          ResponseID: response.Id!,
          ResponseGUID: null,
          ParticipantGUID: null,
          IsDeleted: false,
        }).pipe(
          exhaustMap(() =>
            this.getImagesByActionItem(responseItem.Id).pipe(
              tap(images => {
                response.ActionItems[0].Images = images;
              })
            )
          ),
          map(() => response)
        );
      })
    );
  }

  updatePssrSection(
    pssrId: number,
    {
      sectionId,
      sectionDescription,
      newSection,
    }: {
      sectionId: number;
      sectionDescription: string;
      newSection: Dtos.TaskPlanSection;
    }
  ): Observable<Dtos.PSSRResponse[]> {
    return this.http.put<Dtos.PSSRResponse[]>(
      `${environment.apiUrl}/response/updatePSSRSection/${pssrId}`,
      newSection,
      {
        params: {
          sectionId: `${sectionId}`,
          sectionDescription,
        },
      }
    );
  }
}
